import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CheckCircle, Target, Award, BarChart3, Building2, GraduationCap, ArrowRight, Download } from 'lucide-react';
import { heroData, solutionsData, featuresData, testimonialsData, faqData, ctaData, clientLogos, problemData, solutionData, credibilityData, businessCaseData } from '../mock';
import axios from 'axios';
import { SiteHeader } from '../components/SiteHeader';
import { SiteFooter } from '../components/SiteFooter';
import { PricingSection } from '../components/pricing/PricingSection';
import { FormPrivacyConsent } from '../components/LegalNotices';
import { PRIVACY_PATH } from '../content/legal';
import { BROCHURES } from '../lib/brochures';
import { trackBrochureClick } from '../lib/ga4';
import { contactErrorMessage } from '../lib/contactErrors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    company: '', 
    organisationType: '',
    organisationSize: '',
    interests: [],
    budget: '',
    startDate: '',
    message: '' 
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  useEffect(() => { setReady(true); }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    const value = e.target.value;
    const isChecked = e.target.checked;
    
    setFormData(prev => ({
      ...prev,
      interests: isChecked 
        ? [...prev.interests, value]
        : prev.interests.filter(item => item !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFormError('');
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        organisation_type: formData.organisationType,
        organisation_size: formData.organisationSize,
        interests: formData.interests,
        budget: formData.budget || null,
        start_date: formData.startDate || null,
        message: formData.message || null
      });
      
      if (response.data.success) {
        setFormSubmitted(true);
        setTimeout(() => {
          setFormSubmitted(false);
          setFormData({ 
            name: '', 
            email: '', 
            company: '', 
            organisationType: '',
            organisationSize: '',
            interests: [],
            budget: '',
            startDate: '',
            message: '' 
          });
        }, 3000);
      }
    } catch (error) {
      setFormError(contactErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const askAboutPilot = (programme) => {
    setFormData((previous) => ({ ...previous, message: previous.message || `Ask about a 3-month pilot: ${programme}` }));
  };

  return (
    <div className="landing-page">
      <SiteHeader />
      <main data-testid="home-page-content">

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-8">
            <div className="inline-block px-4 py-2 bg-[#604f9b] text-white rounded-full font-semibold text-sm mb-4">
              GenMoney
            </div>
            <h1 data-testid="home-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {heroData.headline}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {heroData.subheadline}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-[#f49e38] hover:bg-[#e08a28] text-white px-8 py-6 text-lg">
                <a data-testid="hero-free-session-link" href={heroData.calendlyLink} target="_blank" rel="noopener noreferrer">Book a free session <ArrowRight aria-hidden="true" className="ml-2 h-5 w-5" /></a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-[#f49e38] text-[#a65c04] hover:bg-orange-50 px-8 py-6 text-lg h-auto whitespace-normal text-center">
                <a data-testid="hero-pricing-link" href="#pricing">{heroData.ctaSecondary}</a>
              </Button>
            </div>
            <p data-testid="hero-booking-clarification" className="text-sm text-gray-600">Book a 30-minute call to discuss the free 45-minute session.</p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{problemData.headline}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {problemData.problems.map((problem) => (
              <Card key={problem.id} className="border-2 border-gray-200 hover:border-[#7c4a97] hover:shadow-xl transition-all">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{problem.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{problem.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-[#f49e38] hover:bg-[#e08a28] text-white px-8 py-6">
              <a data-testid="problem-booking-link" href={heroData.calendlyLink} target="_blank" rel="noopener noreferrer">Book a free discovery call <ArrowRight aria-hidden="true" className="ml-2 h-5 w-5" /></a>
            </Button>
          </div>
        </div>
      </section>

      {/* Solution Overview Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{solutionData.headline}</h2>
            <p className="text-xl text-gray-600">{solutionData.subheadline}</p>
          </div>
          <Card className="border-2 border-[#7c4a97] shadow-xl">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-4">
                {solutionData.capabilities.map((capability, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-[#7c4a97] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{capability}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 mt-8 text-lg leading-relaxed border-t pt-6">
                {solutionData.description}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" data-testid="solutions-section" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 data-testid="solutions-heading" className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Tailored Solutions for Your Organisation</h2>
            <p data-testid="solutions-description" className="text-xl text-gray-600">Flexible programmes designed for employers and universities</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {solutionsData.map((solution) => (
              <Card key={solution.id} data-testid={`solution-${solution.id}-card`} className="border-2 hover:border-[#7c4a97] hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-3">
                    {solution.id === 1 ? (
                      <Building2 className="h-10 w-10 text-[#7c4a97]" />
                    ) : (
                      <GraduationCap className="h-10 w-10 text-[#7c4a97]" />
                    )}
                    <CardTitle data-testid={`solution-${solution.id}-title`} className="text-2xl">{solution.title}</CardTitle>
                  </div>
                  <CardDescription data-testid={`solution-${solution.id}-subtitle`} className="text-lg font-semibold text-gray-700">{solution.subtitle}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p data-testid={`solution-${solution.id}-description`} className="text-gray-600 leading-relaxed">{solution.description}</p>
                  <ul className="space-y-3">
                    {solution.benefits.map((benefit, idx) => (
                      <li key={idx} data-testid={`solution-${solution.id}-benefit-${idx}`} className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-[#7c4a97] mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4">
                    {solution.id === 1 ? (
                      <a 
                        data-testid="employer-brochure-link"
                        href={BROCHURES.employer.path}
                        onClick={() => trackBrochureClick('employer')}
                        onAuxClick={(event) => { if (event.button === 1) trackBrochureClick('employer'); }}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-full px-6 py-3 bg-[#7c4a97] hover:bg-[#6a3f85] text-white font-semibold rounded-lg transition-colors"
                      >
                        <Download className="h-5 w-5 mr-2" />
                        Download Employer Brochure
                      </a>
                    ) : (
                      <a 
                        data-testid="university-brochure-link"
                        href={BROCHURES.university.path}
                        onClick={() => trackBrochureClick('university')}
                        onAuxClick={(event) => { if (event.button === 1) trackBrochureClick('university'); }}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-full px-6 py-3 bg-[#7c4a97] hover:bg-[#6a3f85] text-white font-semibold rounded-lg transition-colors"
                      >
                        <Download className="h-5 w-5 mr-2" />
                        Download University Brochure
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Clients Section */}
      <section className="py-16 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{credibilityData.headline}</h2>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-12 md:gap-16">
            {clientLogos.map((client) => (
              <div key={client.id} className="grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100">
                <img 
                  src={client.imageUrl} 
                  alt={client.name} 
                  className="h-28 w-28 md:h-32 md:w-32 object-contain"
                  title={client.name}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Testimonials Section */}
      <section id="testimonials" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Real feedback from individuals who've transformed their financial wellbeing</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonialsData.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white hover:shadow-xl transition-shadow border-2 border-gray-100 flex flex-col h-full">
                <CardContent className="pt-6 flex flex-col h-full">
                  <Award className="h-8 w-8 text-[#7c4a97] mb-4" />
                  <p className="text-gray-700 italic leading-relaxed flex-grow mb-4">"{testimonial.quote}"</p>
                  <div className="border-t pt-4 mt-auto">
                    <p className="font-bold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    {testimonial.company && (
                      <p className="text-sm text-[#7c4a97] font-semibold">{testimonial.company}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <PricingSection onPilot={askAboutPilot} />

      {/* Business Case Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{businessCaseData.headline}</h2>
            <p className="text-xl text-gray-600">{businessCaseData.subheadline}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {businessCaseData.outcomes.map((outcome) => (
              <Card key={outcome.id} className="border-2 border-gray-200 hover:border-[#7c4a97] hover:shadow-xl transition-all text-center">
                <CardContent className="pt-8 pb-6">
                  <BarChart3 className="h-16 w-16 text-[#7c4a97] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{outcome.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{outcome.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-[#f49e38] hover:bg-[#e08a28] text-white px-8 py-6">
              <a data-testid="business-case-booking-link" href={heroData.calendlyLink} target="_blank" rel="noopener noreferrer">Book a free discovery call <ArrowRight aria-hidden="true" className="ml-2 h-5 w-5" /></a>
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Choose GenMoney?</h2>
            <p className="text-xl text-gray-600">Trusted expertise delivering measurable impact</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuresData.map((feature) => (
              <div key={feature.id} className="p-6 rounded-lg border border-gray-200 hover:border-[#7c4a97] hover:shadow-lg transition-all space-y-3">
                <Target className="h-12 w-12 text-[#7c4a97]" />
                <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Stay Informed with Our Weekly Newsletter</h2>
            <p className="text-xl text-gray-600">Practical financial wellbeing insights delivered to your inbox every week</p>
          </div>
          
          <Card className="border-2 border-[#7c4a97] shadow-xl overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Left side - Newsletter description */}
                <div className="bg-white p-8 md:p-10 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">GenMoney Weekly Insights</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Join hundreds of HR leaders, wellbeing professionals, and university staff receiving our weekly newsletter packed with:
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-[#7c4a97] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Latest financial wellbeing trends and research</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-[#7c4a97] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Practical tips for supporting your people</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-[#7c4a97] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Case studies and success stories</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-[#7c4a97] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Exclusive resources and tools</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-500">Unsubscribe anytime. No spam, ever.</p>
                </div>
                
                {/* Right side - Mailchimp embed */}
                <div className="bg-gray-50 p-8 md:p-10 flex flex-col justify-center">
                  <div id="mc_embed_signup" style={{background: 'transparent', width: '100%'}}>
                    <form data-testid="newsletter-form" action="https://genmoney.us14.list-manage.com/subscribe/post?u=43b594ff8686c9e47fc0137ad&amp;id=3e1330dd68&amp;f_id=00408ee1f0" method="post" id="mc-embedded-subscribe-form" name="mc-embedded-subscribe-form" className="validate" target="_blank" rel="noopener noreferrer">
                      <div id="mc_embed_signup_scroll">
                        <h3 data-testid="newsletter-heading" className="text-xl font-bold text-gray-900 mb-4">Subscribe Now</h3>
                        <div className="mc-field-group mb-4">
                          <label htmlFor="mce-EMAIL" className="block text-sm font-semibold text-gray-700 mb-2">
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <input 
                            data-testid="newsletter-email-input"
                            type="email" 
                            name="EMAIL" 
                            autoComplete="email"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7c4a97] focus:border-transparent transition-all required email" 
                            id="mce-EMAIL" 
                            required 
                            placeholder="your.email@company.co.uk"
                          />
                        </div>
                        <div id="mce-responses" className="clear">
                          <div data-testid="newsletter-error-response" className="response" id="mce-error-response" style={{display: 'none'}}></div>
                          <div data-testid="newsletter-success-response" className="response" id="mce-success-response" style={{display: 'none'}}></div>
                        </div>
                        <div aria-hidden="true" style={{position: 'absolute', left: '-5000px'}}>
                          <input data-testid="newsletter-honeypot-input" type="text" name="b_43b594ff8686c9e47fc0137ad_3e1330dd68" tabIndex="-1" autoComplete="off" />
                        </div>
                        <div className="clear">
                          <input 
                            data-testid="newsletter-submit-button"
                            type="submit" 
                            name="subscribe" 
                            id="mc-embedded-subscribe" 
                            className="w-full bg-[#f49e38] hover:bg-[#e08a28] text-white font-semibold py-3 px-6 rounded-lg cursor-pointer transition-colors"
                            value="Subscribe to Newsletter"
                          />
                        </div>
                        <p className="mt-4 text-sm text-gray-600"><a data-testid="newsletter-privacy-link" href={PRIVACY_PATH} target="_blank" rel="noopener noreferrer" className="text-[#7c4a97] underline underline-offset-2">Privacy Notice</a></p>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know about our programmes</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`} className="border rounded-lg px-6 hover:border-[#7c4a97] transition-colors">
                <AccordionTrigger data-testid={`faq-${faq.id}-trigger`} className="text-left text-lg font-semibold text-gray-900 hover:text-[#7c4a97]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-${faq.id}-answer`} forceMount className="text-gray-600 leading-relaxed pt-2">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#7c4a97] to-[#604f9b]">
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white">{ctaData.headline}</h2>
          <p className="text-xl text-purple-50 leading-relaxed">{ctaData.description}</p>
          <Button asChild size="lg" className="bg-white text-[#a65c04] hover:bg-gray-100 px-8 py-6 text-lg font-semibold">
            <a data-testid="final-booking-link" href={heroData.calendlyLink} target="_blank" rel="noopener noreferrer">Book a free session <ArrowRight aria-hidden="true" className="ml-2 h-5 w-5" /></a>
          </Button>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-xl text-gray-600">Tell us about your organisation's needs and we'll create a tailored proposal</p>
          </div>
          {formSubmitted ? (
            <div data-testid="contact-success" role="status" className="bg-emerald-50 border-2 border-[#7c4a97] rounded-lg p-8 text-center">
              <CheckCircle className="h-16 w-16 text-[#7c4a97] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
              <p className="text-gray-600">We've received your enquiry and will be in touch within 24 hours.</p>
            </div>
          ) : (
            <form data-testid="contact-form" method="post" onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6 sm:p-8 space-y-6">
              <fieldset disabled={!ready || submitting} className="min-w-0 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    data-testid="contact-name-input"
                    name="name"
                    maxLength={200}
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7c4a97] focus:border-transparent transition-all"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Work Email *</label>
                  <input
                    type="email"
                    id="email"
                    data-testid="contact-email-input"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7c4a97] focus:border-transparent transition-all"
                    placeholder="john@company.co.uk"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">Organisation Name *</label>
                  <input
                    type="text"
                    id="company"
                    data-testid="contact-company-input"
                    name="company"
                    maxLength={200}
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7c4a97] focus:border-transparent transition-all"
                    placeholder="Company or University Name"
                  />
                </div>
                <div>
                  <label htmlFor="organisationType" className="block text-sm font-semibold text-gray-700 mb-2">Organisation Type *</label>
                  <select
                    id="organisationType"
                    data-testid="contact-organisation-type-select"
                    name="organisationType"
                    value={formData.organisationType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7c4a97] focus:border-transparent transition-all bg-white"
                  >
                    <option value="">Select type...</option>
                    <option value="employer">Employer / Corporate</option>
                    <option value="university">University / Higher Education</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="organisationSize" className="block text-sm font-semibold text-gray-700 mb-2">Organisation Size *</label>
                  <select
                    id="organisationSize"
                    data-testid="contact-organisation-size-select"
                    name="organisationSize"
                    value={formData.organisationSize}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7c4a97] focus:border-transparent transition-all bg-white"
                  >
                    <option value="">Select size...</option>
                    <option value="100-500">100-500 employees/students</option>
                    <option value="500-1000">500-1,000 employees/students</option>
                    <option value="1000-5000">1,000-5,000 employees/students</option>
                    <option value="5000+">5,000+ employees/students</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="budget" className="block text-sm font-semibold text-gray-700 mb-2">Estimated Budget</label>
                  <select
                    id="budget"
                    data-testid="contact-budget-select"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7c4a97] focus:border-transparent transition-all bg-white"
                  >
                    <option value="">Select budget...</option>
                    <option value="under-5k">Under £5,000</option>
                    <option value="5k-10k">£5,000 - £10,000</option>
                    <option value="10k-25k">£10,000 - £25,000</option>
                    <option value="25k+">£25,000+</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Programme Interests (select all that apply)</label>
                <div className="grid md:grid-cols-2 gap-3">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      value="budgeting"
                      data-testid="contact-interest-budgeting"
                      checked={formData.interests.includes('budgeting')}
                      onChange={handleCheckboxChange}
                      className="w-4 h-4 text-[#7c4a97] border-gray-300 rounded focus:ring-[#7c4a97]"
                    />
                    <span className="text-gray-700">Budgeting & Saving</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      value="debt"
                      data-testid="contact-interest-debt"
                      checked={formData.interests.includes('debt')}
                      onChange={handleCheckboxChange}
                      className="w-4 h-4 text-[#7c4a97] border-gray-300 rounded focus:ring-[#7c4a97]"
                    />
                    <span className="text-gray-700">Debt Management</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      value="pensions"
                      data-testid="contact-interest-pensions"
                      checked={formData.interests.includes('pensions')}
                      onChange={handleCheckboxChange}
                      className="w-4 h-4 text-[#7c4a97] border-gray-300 rounded focus:ring-[#7c4a97]"
                    />
                    <span className="text-gray-700">Pensions & Retirement</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      value="investing"
                      data-testid="contact-interest-investing"
                      checked={formData.interests.includes('investing')}
                      onChange={handleCheckboxChange}
                      className="w-4 h-4 text-[#7c4a97] border-gray-300 rounded focus:ring-[#7c4a97]"
                    />
                    <span className="text-gray-700">Investing Basics</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      value="resilience"
                      data-testid="contact-interest-resilience"
                      checked={formData.interests.includes('resilience')}
                      onChange={handleCheckboxChange}
                      className="w-4 h-4 text-[#7c4a97] border-gray-300 rounded focus:ring-[#7c4a97]"
                    />
                    <span className="text-gray-700">Financial Resilience</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      value="custom"
                      data-testid="contact-interest-custom"
                      checked={formData.interests.includes('custom')}
                      onChange={handleCheckboxChange}
                      className="w-4 h-4 text-[#7c4a97] border-gray-300 rounded focus:ring-[#7c4a97]"
                    />
                    <span className="text-gray-700">Custom Topics</span>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="startDate" className="block text-sm font-semibold text-gray-700 mb-2">Preferred Start Date</label>
                <input
                  type="text"
                  id="startDate"
                  data-testid="contact-start-date-input"
                  name="startDate"
                  maxLength={100}
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7c4a97] focus:border-transparent transition-all"
                  placeholder="Preferred Start Date"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Additional Details</label>
                <textarea
                  id="message"
                  data-testid="contact-message-input"
                  name="message"
                  maxLength={2000}
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7c4a97] focus:border-transparent transition-all"
                  placeholder="Tell us more about your specific needs, challenges, or questions..."
                ></textarea>
              </div>

              <Button data-testid="contact-submit-button" type="submit" size="lg" className="w-full bg-[#f49e38] hover:bg-[#e08a28] text-white py-6 text-lg font-semibold">
                Submit Enquiry <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              </fieldset>
              {formError && <p data-testid="contact-error" role="alert" className="text-sm text-red-700">{formError}</p>}
              <FormPrivacyConsent testId="contact-privacy-consent" />
              <noscript><a data-testid="contact-noscript-email" href="mailto:hello@genmoney.co.uk">hello@genmoney.co.uk</a></noscript>
            </form>
          )}
        </div>
      </section>

      </main>
      <SiteFooter />
    </div>
  );
};

export default Landing;